// Customers tab — LTV, segments, repeat rate.
import { useEffect, useState } from "react";
import { getCustomers } from "@/lib/analytics";
import { fmtInt, fmtMoney, fmtPct, KpiCard } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import { Users, UserPlus, Repeat, Crown, Heart, Star } from "lucide-react";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E" };

export default function CustomersTab({ range }) {
  const [data, setData] = useState(null);
  const [seg, setSeg] = useState("top");
  useEffect(() => { getCustomers(range, 100).then(setData); }, [JSON.stringify(range)]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const t = data.totals;

  const seglist = {
    top: data.top,
    vip: data.segments.vip,
    high_spenders: data.segments.high_spenders,
    loyal: data.segments.loyal,
  };
  const rows = seglist[seg] || [];

  return (
    <div className="space-y-6" data-testid="analytics-customers">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Clienți unici" value={t.customers} icon={Users} color={COLOR.primary} />
        <KpiCard label="Clienți noi" value={t.new} icon={UserPlus} color={COLOR.amber} />
        <KpiCard label="Repeat" value={t.repeat} icon={Repeat} color={COLOR.emerald} />
        <KpiCard label="Rată repeat" value={t.repeat_rate} format="pct" icon={Repeat} color={COLOR.emerald} />
        <KpiCard label="LTV mediu" value={t.avg_ltv} format="money" icon={Crown} color={COLOR.amber} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {[
          { id: "top", label: "Top după venit", icon: Crown },
          { id: "vip", label: "VIP (1000+ RON)", icon: Star },
          { id: "high_spenders", label: "High spenders (500+)", icon: Heart },
          { id: "loyal", label: "Loyal (3+ comenzi)", icon: Repeat },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setSeg(s.id)}
              data-testid={`customers-seg-${s.id}`}
              className={`text-[12px] px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
                seg === s.id ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              <Icon className="h-3 w-3" />
              {s.label}
            </button>
          );
        })}
      </div>

      <Card title="Listă clienți">
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 px-4">Client</th>
                <th className="text-left">Email</th>
                <th className="text-left">Telefon</th>
                <th className="text-right">Comenzi</th>
                <th className="text-right">Venit</th>
                <th className="text-right">Profit</th>
                <th className="text-right">AOV</th>
                <th className="text-right pr-4">Ultima cumpărătură</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-muted-foreground py-10 text-xs">Fără date</td></tr>
              ) : rows.map((c, i) => (
                <tr key={c.customer_id || c.email || i} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="py-2 px-4 font-medium">{c.name}</td>
                  <td className="text-muted-foreground text-xs">{c.email || "—"}</td>
                  <td className="font-mono text-xs">{c.phone || "—"}</td>
                  <td className="text-right tabular-nums">{fmtInt(c.orders)}</td>
                  <td className="text-right tabular-nums font-semibold">{fmtMoney(c.revenue)}</td>
                  <td className="text-right tabular-nums">{fmtMoney(c.profit)}</td>
                  <td className="text-right tabular-nums">{fmtMoney(c.aov)}</td>
                  <td className="text-right text-xs text-muted-foreground tabular-nums pr-4">{(c.last || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
